import { RouteProps } from 'react-router-dom';
import { SubscriptionFeatureFlag } from '@mm/state/controllers/FeatureFlags';
import { landmineNames } from '@mm/constants/landmine-names';
import { NativeIntegrations } from './NativeIntegrations/NativeIntegrations';
import { SlackIntegrations } from './NativeIntegrations/components/SlackIntegrations/SlackIntegrations';
import { ContactSuppression } from './NativeIntegrations/components/ContactSuppressions/ContactSuppression';
import { FacebookAdsIntegrations } from './NativeIntegrations/components/FacebookAdsIntegrations/FacebookAdsIntegrations';
import { IntegrationLinkType } from './IntegrationsPanel/IntegrationsPanel';
import { GoogleAdsIntegrations } from './NativeIntegrations/components/GoogleAdsIntegrations/GoogleAdsIntegrations';
import { CaptureActionEnum } from '@mm/state/model/SuppressionSettingsModel';
import { IntegrationTypes } from '@mm/state/model/ConnectionModel';

export type PageSettingDefinition = {
  id: IntegrationLinkType;
  name: string;
  subtitle: string;
  component: RouteProps['component'];
  requiredFeature?: SubscriptionFeatureFlag;
  landmineName: string;
  helpDoc?: string;
};

export type IntegrationListItem = {
  type: IntegrationTypes;
  title: string;
  subtitle: string;
};

export const IntegrationErrors = [
  'merge fields were invalid',
  'api key Invalid',
  'invalid_grant',
  'exceeds grid limits',
];

export const INTEGRATIONS_LIST: IntegrationListItem[] = [
  {
    type: 'klaviyo',
    title: 'Klaviyo',
    subtitle: 'Automatically sync your Customers.ai contacts to Klaviyo',
  },
  {
    type: 'shopify',
    title: 'Shopify',
    subtitle: 'Automatically sync your Customers.ai contacts to Shopify',
  },
  {
    type: 'google_spreadsheet',
    title: 'Google Sheets',
    subtitle:
      'Automatically sync customers data from Customers.ai bot to Google Sheets',
  },
  {
    type: 'csv_file',
    title: 'CSV',
    subtitle: 'Upload CSV Manually',
  },
  {
    type: 'zapier',
    title: 'Zapier',
    subtitle:
      'Choose from our list of pre-built templates to integrate Customers.ai with other applications',
  },
  {
    type: 'default',
    title: 'JSON Webhooks ',
    subtitle: 'Automatically send data from Customers.ai to a JSON Webhook',
  },
  {
    type: 'mailchimp',
    title: 'Mailchimp',
    subtitle: 'Automatically send data from Customers.ai to Mailchimp',
  },
  {
    type: 'gohighlevel',
    title: 'HighLevel',
    subtitle: 'Automatically send data from Customers.ai to HighLevel',
  },
  {
    type: 'salesforce',
    title: 'SalesForce',
    subtitle: 'Automatically sync your Customers.ai contacts to SalesForce',
  },
  {
    type: 'sendlane',
    title: 'Sendlane',
    subtitle: 'Automatically send data from Customers.ai to Sendlane',
  },
  {
    type: 'omnisend',
    title: 'Omnisend',
    subtitle: 'Automatically send data from Customers.ai to Omnisend',
  },
  {
    type: 'hubspot',
    title: 'Hubspot',
    subtitle: 'Automatically send data from Customers.ai to Hubspot',
  },
  {
    type: 'braze',
    title: 'Braze',
    subtitle: 'Automatically send data from Customers.ai to Braze',
  },
  {
    type: 'facebook_ads_audience',
    title: 'Facebook',
    subtitle: 'Automatically send data from Customers.ai to Facebook',
  },
  {
    type: 'google_ads_audience',
    title: 'Google Ads',
    subtitle: 'Automatically send data from Customers.ai to Google Ads',
  },
  {
    type: 'slack',
    title: 'Slack',
    subtitle: 'Automatically send data from Customers.ai to Slack',
  },
  {
    type: 'manual_suppression',
    title: 'Manually add Emails/Domains',
    subtitle: `Choose emails, domains, and phone numbers that your automations will not engage with. 
      We automatically suppress bounces, unsubscribes and other emails we believe to be problematic.`,
  },
];

export const INTEGRATIONS_SECTIONS: PageSettingDefinition[] = [
  {
    id: 'native-integrations',
    name: 'CRM connections',
    subtitle: 'Send contacts directly to your CRM',
    component: NativeIntegrations,
    requiredFeature: 'nativeIntegrations',
    landmineName: landmineNames.INTEGRATIONS_NATIVE_INTEGRATIONS,
    helpDoc: 'https://customers.ai/help/l/en/category/06p3o5upol-integrations',
  },
  {
    id: 'contact-suppression',
    name: 'Existing Contact Suppression',
    subtitle: 'Upload your existing contacts to suppress contact creation',
    component: () =>
      ContactSuppression({ captureAction: CaptureActionEnum.do_not_send }),
    landmineName: landmineNames.INTEGRATIONS_CONTACT_SUPPRESSION,
    requiredFeature: 'contactSuppression',
    helpDoc:
      'https://customers.ai/help/l/en/article/uhl81gxos2-enable-email-suppression-list',
  },
  {
    id: 'unsubscribe-list',
    name: 'Unsubscribe List',
    subtitle:
      'Upload lists of contacts that you do not want to track any data or engagement on',
    component: () =>
      ContactSuppression({
        captureAction: CaptureActionEnum.do_not_capture,
      }),
    landmineName: landmineNames.INTEGRATIONS_UNSUBSCRIBE_LIST,
    requiredFeature: 'contactSuppression',
    helpDoc:
      'https://customers.ai/help/l/en/article/uhl81gxos2-enable-email-suppression-list',
  },
  {
    id: 'facebook',
    name: 'Facebook ',
    subtitle:
      'Send contacts directly to Facebook for retargeting, Lookalikes, and audience building',
    component: FacebookAdsIntegrations,
    landmineName: landmineNames.INTEGRATIONS_FACEBOOK_ADS,
    requiredFeature: 'fbRestore',
    helpDoc: '',
  },
  {
    id: 'google-ads',
    name: 'Google Ads ',
    subtitle: 'Send contacts directly to Google Ads',
    component: GoogleAdsIntegrations,
    landmineName: landmineNames.INTEGRATIONS_GOOGLE_ADS,
    requiredFeature: 'fbRestore',
    helpDoc: '',
  },
  {
    id: 'notifications',
    name: 'Notifications',
    subtitle: 'Get alerts directly to your Slack channel',
    component: SlackIntegrations,
    requiredFeature: 'nativeIntegrations',
    landmineName: landmineNames.INTEGRATIONS_NOTIFICATIONS,
    helpDoc: '',
  },
];
